/**
 * Custom hook for fetching metric data
 * Manages data fetching lifecycle, caching, loading states, and error handling
 *
 * @module MetricDataDevTool/hooks/useMetricData
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchMetricDataWithRetry, createQueryCancellation } from '../services/queryService';

/**
 * Custom hook for metric data fetching
 * @param {string} clientId - Client ID
 * @returns {Object} Hook state and methods
 */
export function useMetricData(clientId) {
  const [allData, setAllData] = useState([]); // Full dataset (all metrics)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ count: 0, hasMore: false, page: 0 });

  // Cache for query results
  const cacheRef = useRef(new Map());

  // Abort controller for cancellation
  const abortControllerRef = useRef(null);

  /**
   * Generate cache key from query parameters
   * Note: selectedMetrics excluded from cache key to enable client-side filtering
   */
  const getCacheKey = useCallback((params) => {
    const { storeId, startDate, endDate } = params;
    return `${storeId}_${startDate}_${endDate}`;
  }, []);

  /**
   * Fetch metric data with caching and progress tracking
   * Note: Fetches ALL metrics for the store/date range, filtering happens client-side
   */
  const fetchData = useCallback(async (params) => {
    const { storeId, startDate, endDate } = params;

    console.log('📊 [useMetricData] fetchData called with:', {
      clientId,
      params,
      hasStoreId: !!storeId,
      hasStartDate: !!startDate,
      hasEndDate: !!endDate
    });

    // Validate parameters
    if (!storeId || !startDate || !endDate) {
      const errorMsg = 'Missing required parameters';
      console.error('❌ [useMetricData] Validation failed:', {
        storeId: !!storeId,
        startDate: !!startDate,
        endDate: !!endDate
      });
      setError(errorMsg);
      return;
    }

    // Check cache
    const cacheKey = getCacheKey(params);
    console.log('🔑 [useMetricData] Cache key:', cacheKey);

    if (cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey);
      console.log('💾 [useMetricData] Using cached data:', {
        cacheKey,
        itemCount: cachedData.length
      });
      setAllData(cachedData);
      setError(null);
      return;
    }

    // Cancel any in-progress request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const { controller, signal } = createQueryCancellation();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setAllData([]);
    setProgress({ count: 0, hasMore: false, page: 0 });

    try {
      console.log('🔄 [useMetricData] Starting query with retry (fetching ALL metrics)...');
      const result = await fetchMetricDataWithRetry({
        clientId,
        storeId,
        startDate,
        endDate,
        signal,
        onProgress: (count, hasMore, page) => {
          console.log(`📈 [useMetricData] Progress update: ${count} items, page ${page}, hasMore: ${hasMore}`);
          setProgress({ count, hasMore, page });
        }
      });

      console.log('✅ [useMetricData] Query successful:', {
        resultCount: result.length,
        cacheKey
      });

      // Cache the result
      cacheRef.current.set(cacheKey, result);

      setAllData(result);
      setError(null);
    } catch (err) {
      console.error('❌ [useMetricData] Query failed:', err);
      if (err.message === 'Query cancelled') {
        // Don't set error for cancelled queries
        return;
      }
      setError(err.message || 'Failed to fetch metric data');
      setAllData([]);
    } finally {
      setLoading(false);
      setProgress({ count: 0, hasMore: false, page: 0 });
      abortControllerRef.current = null;
    }
  }, [clientId, getCacheKey]);

  /**
   * Cancel any in-progress query
   */
  const cancelQuery = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Clear cached data
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    cancelQuery();
    setAllData([]);
    setError(null);
    setLoading(false);
    setProgress({ count: 0, hasMore: false, page: 0 });
  }, [cancelQuery]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelQuery();
    };
  }, [cancelQuery]);

  return {
    allData, // Return full dataset - filtering happens in parent component
    loading,
    error,
    progress,
    fetchData,
    cancelQuery,
    clearCache,
    reset
  };
}
